<template>
  <SidebarNavigation :uri="uri">
    <div class="w-full mx-auto">
      <h2 class="flex justify-center font-bold text-xl sm:text-2xl lg:text-3xl mb-4 sm:mb-6 border-b-2 border-gray-200 pb-3 sm:pb-4 px-4 sm:px-0 text-gray-800">{{ __('Dynamic QR Code Generator') }}</h2>
                        <div class="md:mt-10 sm:mt-3 justify-center flex flex-col lg:flex-row w-full mx-auto card-properties gap-4 lg:gap-6 px-4 sm:px-6 lg:px-0">
                            <div class="card-form w-full lg:w-1/2 mx-auto">
                                <div class="flex flex-wrap pl-0 sm:pl-4 lg:pl-8 items-center w-full pt-4 md:pt-0">
                                    <!-- QR Type Selection -->
                                    <div class="w-full mb-6">
                                        <h3 class="text-xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
                                            <icon name="code" class="w-5 h-5 text-green-600" />
                                            QR Code Type
                                        </h3>
                                        <select v-model="form.qr_type" @change="onQrTypeChange" class="w-full pr-6 pb-4 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all font-medium">
                                            <option value="text">Text / URL</option>
                                            <option value="email">Email</option>
                                            <option value="phone">Phone</option>
                                            <option value="sms">SMS</option>
                                            <option value="wifi">WiFi</option>
                                        </select>
                                    </div>

                                    <!-- Text/URL Input -->
                                    <div v-if="form.qr_type === 'text'" class="flex w-full">
                                        <text-input
                                            v-model="form.qr_text"
                                            @keyup="generateQrWhileStop()"
                                            @blur="clearInput()"
                                            @change="generateWebsiteQR()"
                                            :error="form.errors.qr_text"
                                            class="w-full pr-6 pb-4"
                                            :label="__('Text or URL')"
                                            type="text"
                                            autofocus
                                            autocapitalize="off"
                                            placeholder="Enter text or URL (e.g., https://example.com)"
                                        />
                                    </div>

                                    <!-- Email Input -->
                                    <template v-if="form.qr_type === 'email'">
                                        <div class="flex w-full">
                                            <text-input
                                                v-model="form.email_address"
                                                @keyup="generateQrWhileStop()"
                                                @blur="clearInput()"
                                                @change="generateWebsiteQR()"
                                                :error="form.errors.email_address"
                                                class="w-full pr-6 pb-4"
                                                :label="__('Email Address')"
                                                type="email"
                                                placeholder="example@email.com"
                                            />
                                        </div>
                                        <div class="flex w-full">
                                            <text-input
                                                v-model="form.email_subject"
                                                @keyup="generateQrWhileStop()"
                                                @blur="clearInput()"
                                                @change="generateWebsiteQR()"
                                                :error="form.errors.email_subject"
                                                class="w-full pr-6 pb-4"
                                                :label="__('Subject (Optional)')"
                                                type="text"
                                                placeholder="Email subject"
                                            />
                                        </div>
                                        <div class="flex w-full">
                                            <textarea-input
                                                v-model="form.email_body"
                                                @keyup="generateQrWhileStop()"
                                                @blur="clearInput()"
                                                @change="generateWebsiteQR()"
                                                :error="form.errors.email_body"
                                                class="w-full pr-6 pb-4"
                                                :rows="3"
                                                label="Body (Optional)"
                                                placeholder="Email body text"
                                            />
                                        </div>
                                    </template>

                                    <!-- Phone Input -->
                                    <div v-if="form.qr_type === 'phone'" class="flex w-full">
                                        <text-input
                                            v-model="form.phone_number"
                                            @keyup="generateQrWhileStop()"
                                            @blur="clearInput()"
                                            @change="generateWebsiteQR()"
                                            :error="form.errors.phone_number"
                                            class="w-full pr-6 pb-4"
                                            :label="__('Phone Number')"
                                            type="tel"
                                            placeholder="+1234567890"
                                        />
                                    </div>

                                    <!-- SMS Input -->
                                    <template v-if="form.qr_type === 'sms'">
                                        <div class="flex w-full">
                                            <text-input
                                                v-model="form.sms_number"
                                                @keyup="generateQrWhileStop()"
                                                @blur="clearInput()"
                                                @change="generateWebsiteQR()"
                                                :error="form.errors.sms_number"
                                                class="w-full pr-6 pb-4"
                                                :label="__('Phone Number')"
                                                type="tel"
                                                placeholder="+1234567890"
                                            />
                                        </div>
                                        <div class="flex w-full">
                                            <textarea-input
                                                v-model="form.sms_message"
                                                @keyup="generateQrWhileStop()"
                                                @blur="clearInput()"
                                                @change="generateWebsiteQR()"
                                                :error="form.errors.sms_message"
                                                class="w-full pr-6 pb-4"
                                                :rows="3"
                                                label="Message (Optional)"
                                                placeholder="Pre-filled SMS message"
                                            />
                                        </div>
                                    </template>

                                    <!-- WiFi Input -->
                                    <template v-if="form.qr_type === 'wifi'">
                                        <div class="flex w-full">
                                            <text-input
                                                v-model="form.wifi_ssid"
                                                @keyup="generateQrWhileStop()"
                                                @blur="clearInput()"
                                                @change="generateWebsiteQR()"
                                                :error="form.errors.wifi_ssid"
                                                class="w-full pr-6 pb-4"
                                                :label="__('Network Name (SSID)')"
                                                type="text"
                                                placeholder="MyWiFiNetwork"
                                            />
                                        </div>
                                        <div class="flex w-full">
                                            <text-input
                                                v-model="form.wifi_password"
                                                @keyup="generateQrWhileStop()"
                                                @blur="clearInput()"
                                                @change="generateWebsiteQR()"
                                                :error="form.errors.wifi_password"
                                                class="w-full pr-6 pb-4"
                                                :label="__('Password')"
                                                type="password"
                                                placeholder="WiFi password"
                                            />
                                        </div>
                                        <div class="flex w-full">
                                            <label class="block mb-2 text-sm font-semibold text-gray-700">Security Type</label>
                                            <select v-model="form.wifi_security" @change="generateWebsiteQR()" class="w-full pr-6 pb-4 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all">
                                                <option value="WPA">WPA/WPA2</option>
                                                <option value="WEP">WEP</option>
                                                <option value="nopass">No Password</option>
                                            </select>
                                        </div>
                                    </template>

                                    <!-- Customization Options -->
                                    <div class="flex w-full mt-6 pt-6 border-t border-gray-200">
                                        <div class="w-full">
                                            <h3 class="text-xl font-semibold mb-5 text-gray-800 flex items-center gap-2">
                                                <icon name="palette" class="w-5 h-5 text-green-600" />
                                                Customization
                                            </h3>

                                            <div class="flex w-full mb-5 gap-4">
                                                <div class="w-1/2">
                                                    <label class="block mb-2 text-sm font-semibold text-gray-700">Size</label>
                                                    <select v-model="form.qr_size" @change="generateWebsiteQR()" class="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all">
                                                        <option value="200">Small (200px)</option>
                                                        <option value="300">Medium (300px)</option>
                                                        <option value="400" selected>Large (400px)</option>
                                                        <option value="500">X-Large (500px)</option>
                                                    </select>
                                                </div>
                                                <div class="w-1/2">
                                                    <label class="block mb-2 text-sm font-semibold text-gray-700">Error Correction</label>
                                                    <select v-model="form.error_correction" @change="generateWebsiteQR()" class="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all">
                                                        <option value="L">Low (~7%)</option>
                                                        <option value="M" selected>Medium (~15%)</option>
                                                        <option value="Q">Quartile (~25%)</option>
                                                        <option value="H">High (~30%)</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div class="flex w-full mb-4 gap-4">
                                                <div class="w-1/2">
                                                    <label class="block mb-2 text-sm font-semibold text-gray-700">Foreground Color</label>
                                                    <div class="flex items-center gap-2">
                                                        <input
                                                            v-model="form.foreground_color"
                                                            @change="generateWebsiteQR()"
                                                            type="color"
                                                            class="w-16 h-10 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-500 transition-colors"
                                                        />
                                                        <input
                                                            type="text"
                                                            :value="form.foreground_color"
                                                            @input="form.foreground_color = $event.target.value; generateWebsiteQR()"
                                                            class="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-mono"
                                                            placeholder="#000000"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="w-1/2">
                                                    <label class="block mb-2 text-sm font-semibold text-gray-700">Background Color</label>
                                                    <div class="flex items-center gap-2">
                                                        <input
                                                            v-model="form.background_color"
                                                            @change="generateWebsiteQR()"
                                                            type="color"
                                                            class="w-16 h-10 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-500 transition-colors"
                                                        />
                                                        <input
                                                            type="text"
                                                            :value="form.background_color"
                                                            @input="form.background_color = $event.target.value; generateWebsiteQR()"
                                                            class="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-mono"
                                                            placeholder="#FFFFFF"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Preview Section -->
                            <div class="card-items flex w-full lg:w-1/2 mx-auto py-0 lg:sticky lg:top-6 lg:self-start">
                                <div class="flex flex-wrap flex-col items-center justify-center w-full">
                                    <div class="w-full mb-4">
                                        <h3 class="text-xl font-semibold text-gray-800 flex items-center gap-2 mb-2">
                                            <icon name="aperture" class="w-5 h-5 text-green-600" />
                                            Live Preview
                                        </h3>
                                        <p class="text-xs text-gray-500">See your QR code in real-time</p>
                                    </div>
                                    <div class="qr-preview-container bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-2xl border-4 border-gray-100 w-full max-w-md mx-auto">
                                        <div v-if="loading" class="py-8 flex flex-col justify-center gap-3 items-center" role="status">
                                            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                                            <span class="text-gray-600 font-medium">Generating QR code...</span>
                                        </div>
                                        <div v-else-if="form.qr_code" class="flex flex-col items-center">
                                            <div
                                                class="qr-code-wrapper mb-6 rounded-xl overflow-hidden shadow-lg"
                                                :style="{
                                                    width: form.qr_size + 'px',
                                                    height: form.qr_size + 'px',
                                                    backgroundColor: form.background_color,
                                                    padding: '15px',
                                                    border: '3px solid ' + form.foreground_color,
                                                    borderColor: form.foreground_color + '40'
                                                }"
                                            >
                                                <img
                                                    :src="form.qr_code"
                                                    :alt="'QR Code for ' + getQrContent()"
                                                    class="w-full h-full object-contain"
                                                    :style="{
                                                        filter: form.foreground_color !== '#000000' ? `drop-shadow(0 0 0 ${form.foreground_color})` : 'none'
                                                    }"
                                                    ref="qrImage"
                                                />
                                            </div>
                                            <div class="text-center text-sm text-gray-700 mb-2 max-w-xs break-words font-medium bg-gray-50 px-4 py-2 rounded-lg">
                                                {{ getQrContent() || 'Enter text to generate QR code' }}
                                            </div>
                                            <p class="text-xs text-gray-500">Scan with any QR code reader</p>
                                        </div>
                                        <div v-else class="flex flex-col items-center justify-center py-16 text-gray-400">
                                            <div class="w-32 h-32 mb-6 opacity-50 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
                                                <icon name="code" class="w-16 h-16" />
                                            </div>
                                            <p class="text-lg font-medium">Enter content to generate QR code</p>
                                            <p class="text-sm mt-2">Choose a type and fill in the details</p>
                                        </div>
                                    </div>

                                    <!-- Action Buttons -->
                                    <div class="flex flex-col justify-center gap-3 sm:gap-4 py-4 sm:py-6 w-full px-4 sm:px-0">
                                        <button
                                            v-if="form.qr_code"
                                            class="text-white flex justify-center bg-gradient-to-r from-green-600 to-green-700 border-0 focus:outline-none rounded-lg px-4 sm:px-8 py-3 sm:py-3.5 gap-2 items-center font-semibold shadow-lg hover:shadow-xl hover:from-green-700 hover:to-green-800 active:from-green-800 active:to-green-900 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
                                            :disabled="loading"
                                            @click="downloadQRCode()"
                                        >
                                            <icon name="download" class="w-5 h-5" />
                                            Download QR Code
                                        </button>
                                        <button
                                            v-if="form.qr_code"
                                            class="text-white flex justify-center bg-gradient-to-r from-blue-600 to-blue-700 border-0 focus:outline-none rounded-lg px-4 sm:px-8 py-3 sm:py-3.5 gap-2 items-center font-semibold shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
                                            :disabled="loading"
                                            @click="copyQRCodeToClipboard()"
                                        >
                                            <icon name="file" class="w-5 h-5" />
                                            Copy Image
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
    </div>
    <!-- Loading Process -->
        <div v-if="form.processing" class="processing-overlay">
            <div class="background" />
            <div class="loader">
                <svg width="200px" height="200px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" style="background: none;">
                    <circle cx="75" cy="50" fill="#ffffff" r="6.39718">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.875s" />
                    </circle>
                    <circle cx="67.678" cy="67.678" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.75s" />
                    </circle>
                    <circle cx="50" cy="75" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.625s" />
                    </circle>
                    <circle cx="32.322" cy="67.678" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.5s" />
                    </circle>
                    <circle cx="25" cy="50" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.375s" />
                    </circle>
                    <circle cx="32.322" cy="32.322" fill="#ffffff" r="4.80282">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.25s" />
                    </circle>
                    <circle cx="50" cy="25" fill="#ffffff" r="6.40282">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.125s" />
                    </circle>
                    <circle cx="67.678" cy="32.322" fill="#ffffff" r="7.99718">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="0s" />
                    </circle>
                </svg>
            </div>
        </div>
    <!-- Loading Process -->
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import TextInput from '@/Shared/TextInput'
import Icon from '@/Shared/Icon'
import {toPng } from 'html-to-image';
import TextareaInput from '../../Shared/TextareaInput.vue'
import SidebarNavigation from '../../Shared/SidebarNavigation.vue'

export default {
    components: {
        SidebarNavigation,
        TextareaInput,
        Icon,
        TextInput,
    },
    layout: Layout,
    props: {
        title: String,
        uri: {
            type: String,
            required: false
        },
    },
    remember: 'form',
    data() {
        return {
            loading: false,
            form: this.$inertia.form({
                qr_type: 'text',
                qr_text: '',
                qr_code: null,
                qr_size: '400',
                error_correction: 'M',
                foreground_color: '#000000',
                background_color: '#FFFFFF',
                // Email fields
                email_address: '',
                email_subject: '',
                email_body: '',
                // Phone fields
                phone_number: '',
                // SMS fields
                sms_number: '',
                sms_message: '',
                // WiFi fields
                wifi_ssid: '',
                wifi_password: '',
                wifi_security: 'WPA',
            }),
            timeout_qr: null,
        }
    },
    created() {
        // Set default values
        this.form.qr_text = 'https://example.com'
        this.generateWebsiteQR()
    },
    methods: {
        /**
         * Get the content that will be encoded in the QR code based on type
         */
        getQrContent() {
            switch(this.form.qr_type) {
                case 'text':
                    return this.form.qr_text || ''
                case 'email':
                    return this.form.email_address || ''
                case 'phone':
                    return this.form.phone_number || ''
                case 'sms':
                    return this.form.sms_number || ''
                case 'wifi':
                    return this.form.wifi_ssid || ''
                default:
                    return ''
            }
        },

        /**
         * Build the QR code string based on type
         */
        buildQrString() {
            switch(this.form.qr_type) {
                case 'text':
                    let text = this.form.qr_text || ''
                    // Auto-add https:// if it looks like a URL but doesn't have a protocol
                    if(text && text.indexOf('://') === -1 && (text.indexOf('.') > 0 || text.startsWith('www.'))) {
                        text = 'https://' + text
                    }
                    return text

                case 'email':
                    if(!this.form.email_address) return ''
                    let emailStr = `mailto:${this.form.email_address}`
                    const params = []
                    if(this.form.email_subject) params.push(`subject=${encodeURIComponent(this.form.email_subject)}`)
                    if(this.form.email_body) params.push(`body=${encodeURIComponent(this.form.email_body)}`)
                    if(params.length > 0) emailStr += '?' + params.join('&')
                    return emailStr

                case 'phone':
                    if(!this.form.phone_number) return ''
                    return `tel:${this.form.phone_number}`

                case 'sms':
                    if(!this.form.sms_number) return ''
                    let smsStr = `sms:${this.form.sms_number}`
                    if(this.form.sms_message) {
                        smsStr += `:${encodeURIComponent(this.form.sms_message)}`
                    }
                    return smsStr

                case 'wifi':
                    if(!this.form.wifi_ssid) return ''
                    // WiFi QR format: WIFI:T:WPA;S:NetworkName;P:Password;;
                    const security = this.form.wifi_security || 'WPA'
                    const ssid = this.form.wifi_ssid
                    const password = this.form.wifi_password || ''
                    return `WIFI:T:${security};S:${ssid};P:${password};;`

                default:
                    return ''
            }
        },

        /**
         * Generate QR code with debouncing
         */
        generateQrWhileStop(){
            clearTimeout(this.timeout_qr)
            const self = this
            this.timeout_qr = setTimeout(function () {
                self.generateWebsiteQR()
            }, 1000) // Reduced to 1 second for better UX
        },

        /**
         * Clear QR generation timeout
         */
        clearInput(){
            clearTimeout(this.timeout_qr)
        },

        /**
         * Generate QR code immediately
         */
        generateWebsiteQR(){
            const qrContent = this.buildQrString()
            if(!qrContent || qrContent.trim() === '') {
                this.form.qr_code = null
                return
            }

            this.loading = true
            this.form.processing = true

            // Send all customization parameters to backend
            const payload = {
                text: qrContent,
                size: this.form.qr_size,
                error_correction: this.form.error_correction,
                foreground_color: this.form.foreground_color,
                background_color: this.form.background_color
            }

            this.axios.post(this.route('generate.qr'), payload).then((response) => {
                if(response.data && response.data.image) {
                    // Add cache busting query parameter
                    const separator = response.data.image.indexOf('?') > -1 ? '&' : '?'
                    this.form.qr_code = response.data.image + separator + 'v=' + Date.now()
                }
                this.loading = false
                this.form.processing = false
            }).catch((error) => {
                console.error('QR code generation error:', error)
                this.loading = false
                this.form.processing = false
                alert('Error generating QR code. Please try again.')
            })
        },

        /**
         * Handle QR type change
         */
        onQrTypeChange() {
            // Clear current QR code when type changes
            this.form.qr_code = null
            // Generate new QR if there's content
            setTimeout(() => {
                if(this.getQrContent()) {
                    this.generateWebsiteQR()
                }
            }, 100)
        },

        /**
         * Download QR code as PNG
         */
        downloadQRCode() {
            if(!this.form.qr_code) return

            this.loading = true
            const img = new Image()
            img.crossOrigin = 'anonymous'
            img.onload = () => {
                const canvas = document.createElement('canvas')
                canvas.width = parseInt(this.form.qr_size)
                canvas.height = parseInt(this.form.qr_size)
                const ctx = canvas.getContext('2d')

                // Fill background
                ctx.fillStyle = this.form.background_color
                ctx.fillRect(0, 0, canvas.width, canvas.height)

                // Draw QR code
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

                // Convert to blob and download
                canvas.toBlob((blob) => {
                    const url = URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.download = `qr-code-${Date.now()}.png`
                    link.href = url
                    link.click()
                    URL.revokeObjectURL(url)
                    this.loading = false
                })
            }
            img.onerror = () => {
                // Fallback: try to download the image directly
                const link = document.createElement('a')
                link.download = `qr-code-${Date.now()}.png`
                link.href = this.form.qr_code
                link.click()
                this.loading = false
            }
            img.src = this.form.qr_code
        },

        /**
         * Copy QR code image to clipboard
         */
        async copyQRCodeToClipboard() {
            if(!this.form.qr_code) return

            try {
                const response = await fetch(this.form.qr_code)
                const blob = await response.blob()
                await navigator.clipboard.write([
                    new ClipboardItem({ [blob.type]: blob })
                ])
                alert('QR code copied to clipboard!')
            } catch (err) {
                console.error('Failed to copy QR code:', err)
                alert('Failed to copy QR code. Please try downloading instead.')
            }
        },
    },
}
</script>





